import type { ReactNode } from "react";
import { useId } from "react";
import { Switch } from "@headlessui/react";
import { useStaffMode } from "../../hooks/StaffModeProvider";
import { staffFieldControlSlot, staffFieldRoot } from "./fieldStyles";

export type ToogleSwitchProps = {
  label?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  labelFirst?: boolean;
  labelTop?: boolean;
  name?: string;
  id?: string;
  tooltip?: string;
  className?: string;
  labelClassName?: string;
  error?: boolean;
  errorMessage?: string;
  alignRight?: boolean;
  alignCenter?: boolean;
};

export default function ToogleSwitch ({
  label,
  checked,
  onChange,
  disabled = false,
  labelFirst = false,
  labelTop = false,
  name,
  id,
  tooltip,
  className = "",
  labelClassName = "",
  error = false,
  errorMessage,
  alignRight = false,
  alignCenter = false,
}: ToogleSwitchProps) {
  const generatedId = useId();
  const switchId = id ?? name ?? generatedId;
  const { isStaffMode } = useStaffMode();
  const brandAccent = !isStaffMode;

  const track = (
    <Switch
      id={switchId}
      name={name}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className={`relative inline-flex h-6 w-12 shrink-0 cursor-pointer items-center rounded-full !border px-[3px] transition-[background-color,border-color] duration-300 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 
        ${brandAccent
          ? "focus-visible:ring-primary"
          : "focus-visible:ring-secondary"
        } ${checked
          ? brandAccent
            ? "!border-primary bg-primary"
            : "!border-secondary bg-secondary"
          : "!border-gray-300 bg-white"
        }`}
    >
      <span className="sr-only">
        {typeof label === "string" ? label : "Toggle"}
      </span>
      <span
        aria-hidden
        className={`pointer-events-none absolute left-[3px] top-1/2 h-4 w-4 -translate-y-1/2 rounded-full shadow-sm transition-[transform,background-color] duration-300 ease-out will-change-transform ${checked ? "translate-x-[25px] bg-white" : "translate-x-0 bg-gray-400"
          }`}
      />
    </Switch>
  );

  const labelNode =
    label != null && label !== "" ? (
      <Switch.Label
        title={tooltip ?? (typeof label === "string" ? label : undefined)}
        className={`min-w-0 flex-1 select-none text-md font-semibold leading-snug transition-colors ${disabled
          ? "cursor-not-allowed text-gray-400"
          : `cursor-pointer text-gray-font ${brandAccent ? "hover:text-primary" : "hover:text-secondary"
          }`
          } ${labelClassName}`}
      >
        {label}
      </Switch.Label>
    ) : null;

  const hasTopLabel = labelTop && labelNode != null;
  const trackAlign = alignCenter
    ? "justify-center"
    : alignRight
      ? "justify-end"
      : "justify-start";

  return (
    <div
      className={`${staffFieldRoot} ${hasTopLabel ? "w-full" : "w-fit"} min-w-0 ${className}`}
    >
      <Switch.Group>
        {hasTopLabel ? (
          <>
            <div className={`shrink-0 w-full px-1 ${alignCenter ? "text-center" : ""}`}>{labelNode}</div>
            <div className={className + staffFieldControlSlot}>
              <div className={`flex min-w-0 max-w-full ${trackAlign} items-center gap-3 px-1 py-0.5`}>
                {track}
              </div>
            </div>
          </>
        ) : (
          <div className={className + staffFieldControlSlot}>
            <div className={`flex min-w-0 max-w-full flex-nowrap items-center gap-3 px-1 py-0.5`}>
              {labelFirst ? (
                <>
                  {labelNode}
                  {track}
                </>
              ) : (
                <>
                  {track}
                  {labelNode}
                </>
              )}
            </div>
          </div>
        )}
      </Switch.Group>

      {error && errorMessage && (
        <span className="shrink-0 text-sm text-primary">{errorMessage}</span>
      )}
    </div>
  );
}
